"use client";
import { FilterOptions, useSearch } from "@/context/SearchContext";
import { useLocale } from "@/context/LocaleContext";
import Image from "next/image";
import { I18nData } from "@/types/i18n";
import { useEffect, useState } from "react";
import FilterIcon from "../../../public/filter.png";

interface TableHeaderProps {
  paymentTitle?: string;
  i18nData?: I18nData;
}

export default function TableHeader({ paymentTitle, i18nData }: TableHeaderProps) {
  const { handleFilterChange, filters, currentSearchTerm, originalFilters } =
    useSearch();
  const { locale } = useLocale();
  const hasFilters =
    originalFilters?.imprint ||
    originalFilters?.payment ||
    originalFilters?.licenses ||
    originalFilters?.publishing_model;
  const showFilters = !!currentSearchTerm && !!hasFilters;
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  useEffect(() => {
    if (!showFilters) {
      setOpenFilter(null);
    }
  }, [showFilters]);

  const toggleFilter = (filterName: string) => {
    setOpenFilter((prev) => (prev === filterName ? null : filterName));
  };

  const handleOptionChange = (key: keyof FilterOptions, value: string) => {
    handleFilterChange(key, value);
    setOpenFilter(null);
  };

  // Get display name for filter option from i18n data, fallback to original value
  const getFilterOptionDisplayName = (filterKey: keyof FilterOptions, optionValue: string): string => {
    const localeData = i18nData?.[locale];
    
    switch (filterKey) {
      case 'payment':
        return localeData?.payments?.[optionValue]?.name || optionValue;
      case 'licenses':
        return localeData?.licenses?.[optionValue]?.name || optionValue;
      case 'publishing_model':
        return localeData?.publishing_models?.[optionValue]?.name || optionValue;
      default:
        return optionValue;
    }
  };

  const columnHeaders: {
    id: string;
    key: keyof FilterOptions;
    label: string;
    options: string[];
  }[] = [
    {
      id: "imprint",
      key: "imprint",
      label: i18nData?.[locale]?.imprint || "Imprint",
      options: originalFilters?.imprint || [],
    },
    {
      id: "payment",
      key: "payment",
      label: i18nData?.[locale]?.payment_title ?? paymentTitle ?? "Payment",
      options: originalFilters?.payment || [],
    },
    {
      id: "license",
      key: "licenses",
      label: i18nData?.[locale]?.license_options || "License options",
      options: originalFilters?.licenses || [],
    },
    {
      id: "publishingModel",
      key: "publishing_model",
      label: i18nData?.[locale]?.publishing_model || "Publishing model",
      options: originalFilters?.publishing_model || [],
    },
  ];

  return (
    <thead className="table-header">
      <tr>
        <th id="journals">{i18nData?.[locale]?.journals || "Journals"}</th>
        {columnHeaders.map(({ id, key, label, options }) => (
          <th key={id} id={id}>
            <div
              className={`dropdown-label ${
                showFilters ? "clickable cursor-pointer" : ""
              } flex items-center justify-between md:justify-center min-h-[44px]`}
              onClick={() => toggleFilter(id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  toggleFilter(id);
                }
              }}
              role={showFilters ? "button" : undefined}
              tabIndex={showFilters ? 0 : undefined}
              aria-label={showFilters ? `${label} filter button` : undefined}
            >
              <span>{label}</span>
              {showFilters && (
                <Image
                  className={`dropdown-icon dropdown-icon-element`}
                  alt="dropdown icon"
                  src={FilterIcon}
                  width={25}
                  height={25}
                />
              )}
            </div>
            {showFilters && openFilter === id && (
              <ul
                id={`${id}Select`}
                className="selectDropdown"
                role="listbox"
                aria-labelledby={id}
              >
                {options.map((option, index) => (
                  <div className="selectOption" key={index}>
                    <input
                      type="checkbox"
                      id={`${id}-${option}`}
                      name={id}
                      value={option}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleOptionChange(key, option);
                        }
                      }}
                      onChange={() => handleOptionChange(key, option)}
                      checked={filters[key]?.includes(option)}
                    />
                    <label htmlFor={`${id}-${option}`}>
                      {getFilterOptionDisplayName(key, option)}
                    </label>
                  </div>
                ))}
              </ul>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );
}
